import type { LocaleType } from './Locale';
import { numberSettings } from './settings';

const includeThousandSeparator = (
  locale: LocaleType,
  number: number,
  precision: number,
): string => {
  const t = numberSettings[locale].thousand;
  const delimiter = numberSettings[locale].delimiter;
  const prefix = number < 0 ? '-' : '';

  const parts = Math.abs(number).toFixed(precision).toString().split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, t);
  return prefix + parts.join(delimiter);
};

/**
 * Replaces all occurrences of `from` with `to` in string `s`.
 * @param {string} s - A string to replace tokens in.
 * @param {string} from - A string that is replaced by `to`.
 * @param {string} to - A string that replaces the substring `from`.
 * @return {string} A new string with all substrings that match `from` replaced by `to`.
 */
export const replaceAll = (s: string, from: string, to: string): string => {
  let target = s;
  let i = target.indexOf(from);

  while (i !== -1) {
    target = target.replace(from, to);
    i = target.indexOf(from);
  }

  return target;
};

/**
 * Return a string representaton of an integer number, e.g. '1.200'.
 * @param {Locale} locale - A string representing the locale.
 * @param {number} number - The number to format.
 * @param {bool} round - Round the number or floor it. Default is round.
 * @return {string} String that contains the formatted number.
 */
export const formatInt = (
  locale: LocaleType,
  number: number,
  round?: boolean,
): string => {
  let n = number;
  if (round !== undefined && !round) {
    n = Math.floor(n);
  }

  return includeThousandSeparator(locale, n, 0);
};

/**
 * Return a string representaton of an floating number, e.g. '1.200,00'.
 * @param {Locale} locale - A string representing the locale.
 * @param {number} n - The number to format.
 * @param {number} prec - Precision. Default is 2.
 * @param {boolean} strip - Strip trailing zeros from fraction. Default is false.
 * @return {string} String that contains the formatted number.
 */
export const formatFloat = (
  locale: LocaleType,
  number: number,
  prec?: number,
  strip?: boolean,
) => {
  let n = number;
  const precision = prec !== undefined ? prec : 2;

  if (precision) {
    const p = Math.pow(10, precision); // eslint-disable-line
    n = Math.round(n * p) / p;
  }

  const formatted = includeThousandSeparator(locale, n, precision);

  if (strip && precision > 0) {
    // Remove tracing zeros and commas
    let stripped = formatted.replace(/0+$/, '').replace(/,+$/, '');

    // Remove tracing . if any (don't know how to with regex)
    if (stripped.charAt(stripped.length - 1) === '.') {
      stripped = stripped.substr(0, stripped.length - 1);
    }

    return stripped;
  }

  return formatted;
};

/**
 * Return a string representation of a numeric value as a currency string with precision
 * based on the currencyCode.
 * Precision is 2 for all currency codes except for ISK where it is 0.
 * @param {number} amount - The amount to format
 * @param {string} currencyCode - The amount currency code
 * @param {Locale} locale - The locale
 * @param {boolean} suffix - If the currency code should be displayed with the amount
 * @param {customPrecision} precision - determines if customized precision is used
 * @return {string} String containing the formatted money
 */
export const formatMoney = (
  locale: LocaleType,
  amount: number,
  currencyCode: string,
  suffix?: boolean,
  customPrecision?: number,
): string | null => {
  if (amount !== null && amount !== undefined && currencyCode && locale) {
    const precision = currencyCode === 'ISK' ? 0 : 2;
    const amountFormatted = formatFloat(
      locale,
      amount,
      customPrecision !== undefined ? customPrecision : precision,
    );
    return suffix ? `${amountFormatted} ${currencyCode}` : amountFormatted;
  }

  return null;
};

/**
 * Return a string representaton of an percent,
 * e.g. '12,4%. numberSettings is multiplied with 100 before formatting'.
 * @param {Locale} locale - A string representing the locale.
 * @param {number} n - The number to format.
 * @param {number} prec - Precision. Default is 2.
 * @return {string} String that contains the formatted percent value.
 */
export const formatPercent = (
  locale: LocaleType,
  n: number,
  prec?: number,
): string => formatFloat(locale, n * 100, prec);

/**
 * Parse a floating number from a string.
 * @param {Locale} locale - A string representing the locale.
 * @param {string} s - The string to parse.
 * @return {number} A number value.
 */
export const toFloat = (locale: LocaleType, s: string): number => {
  const Settings = numberSettings[locale];
  let string = replaceAll(s, Settings.thousand, '');
  const d = Settings.delimiter;
  if (d !== '.') {
    string = string.replace(d, '.');
  }
  return parseFloat(string);
};

/**
 * Parse an integer number from a string.
 * @param {Locale} locale - A string representing the locale.
 * @param {string} s - The string to parse.
 * @param {bool} round - Round the number or floor it. Default is round.
 * @return {number} A number value.
 */
export const toInt = (
  locale: LocaleType,
  s: string,
  round?: boolean,
): number => {
  const shouldRound = round === undefined ? true : round;
  let f = toFloat(locale, s);
  if (shouldRound) {
    f = Math.round(f);
  } else {
    f = Math.floor(f);
  }
  return f;
};
